import { CheckCheck, ChevronRight } from "lucide-react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import NeedsYouList from "@/components/needsYou/NeedsYouList";
import Badge from "@/components/ui/Badge";
import Tabs from "@/components/ui/Tabs";
import { useNeedsYou, useNeedsYouUpdates } from "@/hooks/useNeedsYou";
import {
  useClearAllNotifications,
  useNotifications,
} from "@/hooks/useNotifications";
import { needsYouUpdateCopy } from "@/lib/needsYouCopy";
import { formatRelative } from "@/lib/utils";
import type { NotificationPriority } from "@/types";

type BellTab = "bell-needs-you" | "bell-updates";

const PRIORITY_VARIANT: Record<
  NotificationPriority,
  "error" | "warning" | "default"
> = {
  high: "error",
  medium: "warning",
  low: "default",
};

/**
 * The bell's panel (in-app design spec § 12 § 3.1 header "bell (count = Needs
 * you)", § 3.3 "bell open (tabs Needs you 3 / Updates 4, both lists drawn)",
 * § 5 item 3). Two tabs:
 *
 * - **Needs you** — the ONE list (`useNeedsYou`, the same query the Dashboard
 *   and Active render), dense: each row its kind word, what needs the user and
 *   its one action. Taking an action closes the panel. A failed read says so
 *   when there is no list to show; a failed refetch keeps the last list.
 * - **Updates** — the news, never counted: the 7-day learning lines
 *   (`?include=updates`, read only while this tab is open) and the
 *   notification feed (`GET /api/v1/workspace/notifications`, read only when
 *   this tab opens), with Clear all. Either read failing says so; the calm
 *   "no updates" line needs BOTH reads to have succeeded empty.
 */
export default function BellPanel({
  appId,
  onClose,
}: {
  appId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<BellTab>("bell-needs-you");
  const needsYou = useNeedsYou(appId);
  const items = needsYou.data?.items ?? [];
  const failed = needsYou.isError && !needsYou.data;

  return (
    <div className="w-[min(400px,calc(100vw-32px))] space-y-2 p-3">
      <Tabs
        value={tab}
        onValueChange={(next) => setTab(next as BellTab)}
        ariaLabel={t("layout.bell.tabsLabel")}
        tabs={[
          {
            value: "bell-needs-you",
            label: t("layout.bell.needsYouTab"),
            count: needsYou.data ? items.length : undefined,
          },
          { value: "bell-updates", label: t("layout.bell.updatesTab") },
        ]}
      >
        {tab === "bell-needs-you" ? (
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {!appId ? (
              <p className="text-ink-soft text-sm">{t("layout.bell.noApp")}</p>
            ) : failed ? (
              <p role="alert" className="text-error-text text-sm">
                {t("dashboard.needsYou.loadError")}
              </p>
            ) : !needsYou.data ? (
              <p className="text-muted text-sm">
                {t("dashboard.needsYou.loading")}
              </p>
            ) : items.length === 0 ? (
              <p className="text-ink-soft text-sm">
                {t("layout.bell.needsYouEmpty")}
              </p>
            ) : (
              <NeedsYouList
                items={items}
                labelledBy="tab-bell-needs-you"
                compact
                onOpen={onClose}
              />
            )}
          </div>
        ) : (
          <BellUpdates appId={appId} onClose={onClose} />
        )}
      </Tabs>
      <div className="border-divider-soft flex justify-end border-t pt-2">
        <Link
          to="/settings/notifications"
          onClick={onClose}
          className="text-primary-text inline-flex min-h-6 items-center gap-1 text-xs font-medium hover:underline"
        >
          {t("layout.bell.settingsLink")}
          <ChevronRight size={12} aria-hidden="true" />
        </Link>
      </div>
    </div>
  );
}

function BellUpdates({
  appId,
  onClose,
}: {
  appId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const news = useNeedsYouUpdates(appId, true);
  const feed = useNotifications();
  const clearAll = useClearAllNotifications();
  const updates = news.data?.updates ?? [];
  const notifications = feed.data?.notifications ?? [];
  const newsFailed = news.isError && !news.data;
  const feedFailed = feed.isError && !feed.data;
  const pending =
    (Boolean(appId) && !news.data && !newsFailed) ||
    (!feed.data && !feedFailed);

  return (
    <div className="max-h-[60vh] space-y-2 overflow-y-auto">
      {/* A failed read is NOT an empty feed: say it, never show calm. */}
      {newsFailed && (
        <p role="alert" className="text-error-text text-sm">
          {t("dashboard.needsYou.loadError")}
        </p>
      )}
      {updates.length > 0 && (
        <ul className="space-y-1">
          {updates.map((update) => {
            const copy = needsYouUpdateCopy(t, update);
            return (
              <li
                key={update.id}
                data-testid={`bell-update-${update.type}`}
                className="border-divider bg-surface-alt flex items-center gap-3 rounded-xl border px-3 py-2"
              >
                <p className="text-ink min-w-0 flex-1 text-sm">{copy.title}</p>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate(update.href);
                  }}
                  className="text-primary-text inline-flex min-h-6 shrink-0 items-center text-xs font-medium hover:underline"
                >
                  {copy.actionLabel}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {feedFailed ? (
        <p
          role="alert"
          className="border-danger-soft bg-danger-bg text-error-text rounded-xl border px-3 py-2 text-sm"
        >
          {t("layout.bell.updatesLoadError")}
        </p>
      ) : pending ? (
        <p className="text-muted text-sm">{t("layout.bell.updatesLoading")}</p>
      ) : notifications.length === 0 ? (
        updates.length === 0 &&
        !newsFailed && (
          <p className="text-ink-soft text-sm">
            {t("layout.bell.updatesEmpty")}
          </p>
        )
      ) : (
        <>
          <div className="flex justify-end">
            <button
              type="button"
              aria-disabled={clearAll.isPending}
              onClick={() => {
                if (clearAll.isPending) return;
                clearAll.mutate(undefined, {
                  // Keyboard focus sits on this button; when the feed empties
                  // the button unmounts, so focus moves to the tab first.
                  onSuccess: () =>
                    document.getElementById("tab-bell-updates")?.focus(),
                  // A failed clear-all otherwise just flips isPending back:
                  // the user would read the idle button as success while the
                  // items survive server-side.
                  onError: () => toast.error(t("layout.bell.clearAllError")),
                });
              }}
              className="text-ink-soft hover:text-ink inline-flex min-h-6 items-center gap-1 rounded-md px-2 py-1 text-xs font-medium aria-disabled:cursor-not-allowed aria-disabled:opacity-60"
            >
              <CheckCheck size={14} aria-hidden="true" />
              {clearAll.isPending
                ? t("layout.bell.clearingButton")
                : t("layout.bell.clearAllButton")}
            </button>
          </div>
          <ul className="space-y-2">
            {notifications.map((n) => (
              <li
                key={n.notification_id}
                className="border-divider bg-surface flex items-start gap-3 rounded-xl border p-3"
              >
                <Badge variant={PRIORITY_VARIANT[n.priority]}>
                  {t(`layout.bell.priority.${n.priority}`)}
                </Badge>
                <div className="min-w-0 flex-1">
                  <p className="text-ink text-sm font-medium">{n.title}</p>
                  <p className="text-ink-soft mt-0.5 text-sm">{n.body}</p>
                </div>
                <span className="text-muted shrink-0 text-xs">
                  {formatRelative(n.occurred_at)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
